/**
 * Which build of an app is running (SPEC §17, 2026-10-23): the suite version both apps share
 * (`pnpm suite:version`), plus the git commit it was built from.
 *
 * The control app warns when the graphics app's **version** differs from its own; the commit
 * is shown for diagnosis but never warns by itself. A different protocol stays a hard stop.
 */
import { z } from 'zod';

export const AppBuildSchema = z.strictObject({
  /** Suite version, `X.Y.Z`. */
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  /** Short git commit hash, or `unknown` (not built from a git checkout). */
  commit: z.string().regex(/^([0-9a-f]{7,40}|unknown)$/),
  /** The checkout had uncommitted changes when it was built (or started). */
  modified: z.boolean(),
});
export type AppBuild = z.infer<typeof AppBuildSchema>;

/** `0.2.0 (abc1234)`, `0.2.0 (abc1234, modified)`, `0.2.0` when the commit is unknown. */
export function formatBuild(b: AppBuild): string {
  if (b.commit === 'unknown') return b.modified ? `${b.version} (modified)` : b.version;
  return `${b.version} (${b.commit}${b.modified ? ', modified' : ''})`;
}

/** True when both builds carry the same suite version (commits may differ). */
export function sameVersion(a: AppBuild, b: AppBuild): boolean {
  return a.version === b.version;
}

/**
 * Reason the graphics app gives when it closes a state socket of another protocol version:
 * `protocol 5 required; graphics 0.2.0`. WebSocket close reasons are limited to 123 bytes.
 */
export function protocolMismatchReason(protocolVersion: number, graphics: AppBuild): string {
  return `protocol ${protocolVersion} required; graphics ${graphics.version}`;
}

/** Reads {@link protocolMismatchReason} back; `null` for any other text (e.g. an older server). */
export function parseProtocolMismatchReason(reason: string): { protocolVersion: number; graphicsVersion: string } | null {
  const m = /^protocol (\d+) required; graphics (\d+\.\d+\.\d+)$/.exec(reason);
  return m ? { protocolVersion: Number(m[1]), graphicsVersion: m[2]! } : null;
}
